import React, { useState, useEffect } from 'react';

import './HealthQuestionnaires.css';
import api from '../../api/api';
import axios from 'axios';
import ToastMessage from "../../utils/mui/righttoptoast";
    const QUESTIONNAIRES = {
      phq9: {
        title: 'PHQ-9 Depression Screener',
        subtitle: 'Patient Health Questionnaire',
        instructions: 'Over the <strong>last 2 weeks</strong>, how often have you been bothered by any of the following problems?',
        questions: [
          'Little interest or pleasure in doing things',
          'Feeling down, depressed, or hopeless',
          'Trouble falling or staying asleep, or sleeping too much',
          'Feeling tired or having little energy',
          'Poor appetite or overeating',
          'Feeling bad about yourself — or that you are a failure or have let yourself or your family down',
          'Trouble concentrating on things, such as reading the newspaper or watching television',
          'Moving or speaking so slowly that other people could have noticed? Or the opposite — being so fidgety or restless that you have been moving around a lot more than usual',
          'Thoughts that you would be better off dead or of hurting yourself in some way'
        ],
        options: [
          { label: 'Not at all', value: 0 },
          { label: 'Several days', value: 1 },
          { label: 'More than half the days', value: 2 },
          { label: 'Nearly every day', value: 3 }
        ],
        extraQuestion: {
          id: 'phq9_extra',
          text: 'If you checked off any problems, how difficult have these problems made it for you to do your work, take care of things at home, or get along with other people?',
          options: [
            { label: 'Not difficult at all', value: 0 },
            { label: 'Somewhat difficult', value: 1 },
            { label: 'Very difficult', value: 2 },
            { label: 'Extremely difficult', value: 3 }
          ]
        },
        score(answers) {
          const total = answers.slice(0, 9).reduce((s, v) => s + (v || 0), 0);
          let level, color, bg;
          if (total <= 4)       { level = 'None – Minimal Depression';     color = '#065f46'; bg = '#ecfdf5'; }
          else if (total <= 9)  { level = 'Mild Depression';               color = '#92400e'; bg = '#fef3c7'; }
          else if (total <= 14) { level = 'Moderate Depression';           color = '#92400e'; bg = '#fef3c7'; }
          else if (total <= 19) { level = 'Moderately Severe Depression';  color = '#991b1b'; bg = '#fee2e2'; }
          else                  { level = 'Severe Depression';             color = '#991b1b'; bg = '#fee2e2'; }
          return { total, level, color, bg,
            text: `Your PHQ-9 score is <strong>${total}/27</strong>. Result: <strong>${level}</strong>. Your provider will review these results at your appointment.` };
        }
      },

      stopbang: {
        title: 'STOP-BANG Sleep Apnea Screener',
        subtitle: 'Obstructive Sleep Apnea Risk Assessment',
        instructions: 'Please answer <strong>Yes</strong> or <strong>No</strong> to each of the following questions.',
        questions: [
          '<strong>S</strong>noring — Do you snore loudly (louder than talking or loud enough to be heard through closed doors)?',
          '<strong>T</strong>ired — Do you often feel tired, fatigued, or sleepy during the daytime?',
          '<strong>O</strong>bserved — Has anyone observed you stop breathing or choking/gasping during your sleep?',
          '<strong>P</strong>ressure — Do you have or are you being treated for high blood pressure?',
          '<strong>B</strong>MI — Is your BMI greater than 35?',
          '<strong>A</strong>ge — Are you older than 50 years?',
          '<strong>N</strong>eck — Is your neck circumference greater than 40 cm (15.7 inches)?',
          '<strong>G</strong>ender — Are you male?'
        ],
        options: [
          { label: 'Yes', value: 1 },
          { label: 'No',  value: 0 }
        ],
        score(answers) {
          const total = answers.reduce((s, v) => s + (v || 0), 0);
          let level, color, bg;
          if (total <= 2)      { level = 'Low Risk for OSA';          color = '#065f46'; bg = '#ecfdf5'; }
          else if (total <= 4) { level = 'Intermediate Risk for OSA'; color = '#92400e'; bg = '#fef3c7'; }
          else                 { level = 'High Risk for OSA';         color = '#991b1b'; bg = '#fee2e2'; }
          return { total, level, color, bg,
            text: `Your STOP-BANG score is <strong>${total}/8</strong>. Result: <strong>${level}</strong>. Your provider will review these results at your appointment.` };
        }
      },

      adhd: {
        title: 'ASRS-v1.1 Adult ADHD Self-Report Scale',
        subtitle: 'World Health Organization Screener — Part A',
        instructions: 'Please answer the questions below, rating yourself on each of the criteria shown, using the scale on the right side of the page. As you answer each question, place an X in the box that best describes how you have felt and conducted yourself <strong>over the past 6 months</strong>.',
        questions: [
          'How often do you have trouble wrapping up the final details of a project, once the challenging parts have been done?',
          'How often do you have difficulty getting things in order when you have to do a task that requires organization?',
          'How often do you have problems remembering appointments or obligations?',
          'When you have a task that requires a lot of thought, how often do you avoid or delay getting started?',
          'How often do you fidget or squirm with your hands or feet when you have to sit down for a long time?',
          'How often do you feel overly active and compelled to do things, like you were driven by a motor?'
        ],
        options: [
          { label: 'Never',     value: 0 },
          { label: 'Rarely',    value: 1 },
          { label: 'Sometimes', value: 2 },
          { label: 'Often',     value: 3 },
          { label: 'Very Often',value: 4 }
        ],
        // ASRS Part A: positives = Sometimes/Often/VeryOften for Q1–3, Often/VeryOften for Q4–6
        score(answers) {
          const thresholds = [2, 2, 2, 3, 3, 3]; // "Sometimes" triggers Q1-3, "Often" triggers Q4-6
          let positives = 0;
          answers.forEach((v, i) => { if ((v || 0) >= thresholds[i]) positives++; });
          const likely = positives >= 4;
          const level = likely ? 'Symptoms Consistent with ADHD' : 'Symptoms Unlikely to Indicate ADHD';
          const color = likely ? '#991b1b' : '#065f46';
          const bg    = likely ? '#fee2e2' : '#ecfdf5';
          return { total: positives, level, color, bg,
            text: `<strong>${positives} of 6</strong> responses fell in the shaded zone. Result: <strong>${level}</strong>. This is a screener only — your provider will discuss next steps at your appointment.` };
        }
      },

      mdq: {
        title: 'MDQ — Mood Disorder Questionnaire',
        subtitle: 'Bipolar Spectrum Screening Tool',
        instructions: 'Has there ever been a period of time when you were not your usual self and… (check all that apply)',
        questions: [
          '…you felt so good or so hyper that other people thought you were not your normal self or you were so hyper that you got into trouble?',
          '…you were so irritable that you shouted at people or started fights or arguments?',
          '…you felt much more self-confident than usual?',
          '…you got much less sleep than usual and found you didn\'t really miss it?',
          '…you were much more talkative or spoke much faster than usual?',
          '…thoughts raced through your head or you couldn\'t slow your mind down?',
          '…you were so easily distracted by things around you that you had trouble concentrating or staying on track?',
          '…you had much more energy than usual?',
          '…you were much more active or did many more things than usual?',
          '…you were much more social or outgoing than usual (e.g., telephoned friends in the middle of the night)?',
          '…you were much more interested in sex than usual?',
          '…you did things that were unusual for you or that other people might have seen as excessive, foolish, or risky?',
          '…spending money got you or your family into trouble?'
        ],
        options: [
          { label: 'Yes', value: 1 },
          { label: 'No',  value: 0 }
        ],
        extraQuestions: [
          {
            id: 'mdq_same_time',
            text: 'If you checked YES to more than one of the above, have several of these ever happened during the same period of time?',
            options: [
              { label: 'Yes', value: 1 },
              { label: 'No',  value: 0 }
            ]
          },
          {
            id: 'mdq_problem',
            text: 'How much of a problem did any of these cause you — like being unable to work; having family, money, or legal troubles; or getting into arguments or fights?',
            options: [
              { label: 'No problem',         value: 0 },
              { label: 'Minor problem',      value: 1 },
              { label: 'Moderate problem',   value: 2 },
              { label: 'Serious problem',    value: 3 }
            ]
          }
        ],
        score(answers, extra) {
          const yesCount = answers.slice(0, 13).reduce((s, v) => s + (v || 0), 0);
          const sameTime = (extra && extra.mdq_same_time) ? extra.mdq_same_time : 0;
          const problem  = (extra && extra.mdq_problem)   ? extra.mdq_problem   : 0;
          const positive = yesCount >= 7 && sameTime === 1 && problem >= 2;
          const level = positive ? 'Positive Screen — Possible Bipolar Disorder' : 'Screen Negative';
          const color = positive ? '#991b1b' : '#065f46';
          const bg    = positive ? '#fee2e2' : '#ecfdf5';
          return { total: yesCount, level, color, bg,
            text: `You endorsed <strong>${yesCount} of 13</strong> symptom items. Result: <strong>${level}</strong>. This is a clinical screener — your provider will review results and discuss further at your appointment.` };
        }
      }
    };



const HealthQuestionnaires = ({ practiceId, rowId, healthCompleted }) => {
  const [activeFormId, setActiveFormId] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [extraAnswers, setExtraAnswers] = useState({});
  const [completed, setCompleted] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const [alertmessage, setAlertMessage] = useState('');
  const [severity, setSeverity] = useState('success');
  const[isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const handlealert = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  useEffect(() => {
    if (rowId !== 0) {
      console.log('rowId updated:', rowId);
      setCompleted(healthCompleted);
    }
  }, [rowId]);
  const formKeys = Object.keys(QUESTIONNAIRES);  
  // const totalCompleted = Object.keys(completed).length;
  const totalCompleted = Object.values(completed).filter(Boolean).length;
  console.log('HealthQuestionnaires ',totalCompleted,' completed forms:', formKeys.length, completed);
  const progressPercent = (totalCompleted / formKeys.length) * 100;

  const handleCardClick = (id) => {
    const q = QUESTIONNAIRES[id];
    const totalQs = q.questions.length + (q.extraQuestion ? 1 : 0);
    setAnswers(new Array(totalQs).fill(null));

    // Reset extraQuestions if present
    if (q.extraQuestions) {
      const extras = {};
      q.extraQuestions.forEach(eq => extras[eq.id] = null);
      setExtraAnswers(extras);
    }

    setResult(null);
    setActiveFormId(id);
  };

  const handleAnswerChange = (index, value) => {
    setAnswers(prev => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  const handleExtraChange = (id, value) => {
    setExtraAnswers(prev => ({ ...prev, [id]: value }));
  };


// const buildPayload = (formId, answers, extraAnswers = {}) => {
//   const payload = { form_name: QUESTIONNAIRES[formId].title };

//   // Map main question answers
//   answers.forEach((ans, i) => {
//     payload[`qanswer_${formId}_${i + 1}`] = 
//       // Convert value to label
//       QUESTIONNAIRES[formId].options.find(opt => opt.value === ans)?.label || null;
//   });

//   // Map extra questions if present
//   if (QUESTIONNAIRES[formId].extraQuestion) {
//     const idx = answers.length; // last index
//     payload[`qanswer_${formId}_${idx}`] =
//       QUESTIONNAIRES[formId].extraQuestion.options.find(opt => opt.value === answers[idx])?.label || null;
//   }

//   if (QUESTIONNAIRES[formId].extraQuestions) {
//     QUESTIONNAIRES[formId].extraQuestions.forEach(eq => {
//       payload[`qanswer_${formId}_${eq.id}`] = 
//         eq.options.find(opt => opt.value === extraAnswers[eq.id])?.label || null;
//     });
//   }

//   return payload;
// };

const buildFormPayload = (formId, answers, extraAnswers = {}) => {
  const formpayload = { form_name: QUESTIONNAIRES[formId].title };

  const qForm = QUESTIONNAIRES[formId];

  // Map main question answers
  qForm.questions.forEach((_, i) => {
    formpayload[`qanswer_${formId}_${i + 1}`] =
      qForm.options.find(opt => opt.value === answers[i])?.label || null;
  });

  // Handle single extraQuestion (like PHQ-9)
  if (qForm.extraQuestion) {
    const extraIdx = qForm.questions.length; // last index in answers array
    formpayload[`qanswer_${formId}_${extraIdx + 1}`] =
      qForm.extraQuestion.options.find(opt => opt.value === answers[extraIdx])?.label || null;
  }

  // Handle multiple extraQuestions (like MDQ)
  if (qForm.extraQuestions) {
    qForm.extraQuestions.forEach((eq, idx) => {
      formpayload[`qanswer_${formId}_${eq.id}`] =
        eq.options.find(opt => opt.value === extraAnswers[eq.id])?.label || null;
    });
  }

  return formpayload;
};

  const handleSubmit = async () => {
  const q = QUESTIONNAIRES[activeFormId];

  let res = null;
  if (q.score) {
    res = q.score(answers, extraAnswers);
    setResult(res);
  }

  // Build payload
  const formpayload = buildFormPayload(activeFormId, answers, extraAnswers);

  const payload = {
    formfield: activeFormId,
    formfielddata: formpayload
  };
  // console.log('Payload to send:', payload);
  // console.log('API endpoint:', `/v1/${practiceId}/onboarding/${rowId}/updateform`);
  //   return; // Skip API call for now
    try {
      setLoading(true);
      const response = await api.post(
        `/v1/${practiceId}/onboarding/${rowId}/updateform`,
        payload
      );

      if (response.status == 200) {
        console.log('Form submitted successfully');
        setAlertMessage(`Form submitted successfully`);
        setCompleted(prev => ({
          ...prev,
          [activeFormId]: true
        }));
        setSeverity("success");
        setOpen(true)
      } else {
        console.error('Failed to submit:', response.data);
        // alert('Failed to submit form. Please try again.');
        setAlertMessage("Failed to submit form. Please try again.");
        setSeverity("error");
        setOpen(true);
      }
    } catch (err) {
      console.error('API error:', err);
      // alert('Error submitting form. Please try again later.');
        setAlertMessage("Error submitting form. Please try again later.");
        setSeverity("error");
        setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  if (activeFormId) {
    const q = QUESTIONNAIRES[activeFormId];
    return (
      <div id="q-screen" className="q-wrap">
        <div className="qs-header">
          <button className="qs-back-btn" onClick={() => setActiveFormId(null)}>←</button>
          <div className="qs-header-text">
            <h2>{q.title}</h2>
            <p>{q.subtitle}</p>
          </div>
        </div>

        <div className="qs-body">
          {q.instructions && <div className="qs-instructions" dangerouslySetInnerHTML={{ __html: q.instructions }} />}

          <div className="qs-questions-container">
            {q.questions.map((text, i) => (
              <div key={i} className="qs-question-block">
                <div className="qs-q-num text-start">Question {i + 1}</div>
                <div className="qs-q-text text-start" dangerouslySetInnerHTML={{ __html: text }} />
                <div className="qs-options">
                  {q.options.map(opt => (
                    <div key={opt.value} className="qs-option">
                      <input
                        type="radio"
                        id={`q_${i}_${opt.value}`}
                        name={`q_${i}`}
                        value={opt.value}
                        checked={answers[i] === opt.value}
                        onChange={() => handleAnswerChange(i, opt.value)}
                      />
                      <label htmlFor={`q_${i}_${opt.value}`}>{opt.label}</label>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {q.extraQuestion && (
              <div className="qs-question-block">
                <div className="qs-q-num text-start">Question {q.questions.length + 1}</div>
                <div className="qs-q-text text-start">{q.extraQuestion.text}</div>
                <div className="qs-options">
                  {q.extraQuestion.options.map(opt => (
                    <div key={opt.value} className="qs-option">
                      <input
                        type="radio"
                        id={`extra_${opt.value}`}
                        name={`extra_${q.extraQuestion.id}`}
                        value={opt.value}
                        checked={answers[q.questions.length] === opt.value}
                        onChange={() => handleAnswerChange(q.questions.length, opt.value)}
                      />
                      <label htmlFor={`extra_${opt.value}`}>{opt.label}</label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {q.extraQuestions && q.extraQuestions.map((eq, idx) => (
              <div key={eq.id} className="qs-question-block">
                <div className="qs-q-num text-start">Question {q.questions.length + idx + 1}</div>
                <div className="qs-q-text text-start">{eq.text}</div>
                <div className="qs-options">
                  {eq.options.map(opt => (
                    <div key={opt.value} className="qs-option">
                      <input
                        type="radio"
                        id={`${eq.id}_${opt.value}`}
                        name={eq.id}
                        value={opt.value}
                        checked={extraAnswers[eq.id] === opt.value}
                        onChange={() => handleExtraChange(eq.id, opt.value)}
                      />
                      <label htmlFor={`${eq.id}_${opt.value}`}>{opt.label}</label>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="qs-submit-row">
            <span className="qs-required-note">All questions are required.</span>
            <button className="qs-submit-btn" onClick={handleSubmit} disabled={loading}>
              {loading ? 'Submitting...' : 'Submit & Continue →'}
            </button>
          </div>

          {result && (
            <div className="qs-result" style={{ display: 'block', backgroundColor: result.bg, color: result.color }}>
              <div className="qs-result-title" dangerouslySetInnerHTML={{ __html: result.text }} />
              <button className="qs-result-back-btn" onClick={() => setActiveFormId(null)}>← Back to Questionnaires</button>
            </div>
          )}
        </div>
        <ToastMessage message={alertmessage} severity={severity} open={open} handleClose={handlealert} />
      </div>
    );
  }

  // Hub view
  return (
    <div className="q-wrap">
      <div className="q-hub-header">
        <div className="q-eyebrow">Center for Hypertension</div>
        <h1>Health Questionnaires</h1>
        <p>Please complete each screening below. Your responses help your care team understand your health needs before your visit.</p>
        <div className="q-progress-bar-wrap">
          <div className="q-progress-fill" style={{ width: `${progressPercent}%` }} />
        </div>
        <div className="q-progress-label">{`${totalCompleted} of ${formKeys.length} completed`}</div>
      </div>

      <div className="q-hub-body">
        {totalCompleted < formKeys.length && (
        <div className="q-form-cards">
          {formKeys.map((id, idx) => {
            const q = QUESTIONNAIRES[id];
            return (
              <div
                key={id}
                className={`q-card ${completed[id] ? 'q-card--done q-card--disabled' : ''}`}
                onClick={completed[id] ? undefined : () => handleCardClick(id)}
                style={{ pointerEvents: completed[id] ? 'none' : 'auto' }}
              >
                <div className={`q-card-icon ${completed[id] ? 'q-card-icon--done' : 'q-card-icon--pending'}`}>{q.icon || idx + 1}</div>
                <div className="q-card-meta">
                  <div className="q-card-title">{q.title}</div>
                  <div className="q-card-subtitle">{q.subtitle}</div>
                </div>
                <span className={`q-card-badge ${completed[id] ? 'q-card-badge--done' : 'q-card-badge--pending'}`}>
                  {completed[id] ? 'Done' : 'Pending'}
                </span>
                {!completed[id] && (
                    <span className="q-card-arrow">→</span>
                )}                
              </div>
            );
          })}
        </div>
        )}
        {totalCompleted === formKeys.length && (
          <div className="q-all-done">
            <div className="q-done-icon">✦</div>
            <h2>All Questionnaires Complete</h2>
            <p>Thank you. Your care team at the Center for Hypertension will review your responses before your visit. A confirmation has been sent to your email.</p>
          </div>
        )}

      </div>      
    </div>
  );
};

export default HealthQuestionnaires;